/**
 * Cancel Appointment Modal Component
 *
 * Modal that displays an iframe for canceling appointments via Calendly.
 * Provides overlay functionality and handles appointment refresh on close.
 *
 * @module components/patient-dashboard/CancelAppointmentModal
 */

import React, { useEffect } from 'react';
import { useTheme } from '@/hooks/useTheme';
import { SubtitleText } from '@/components/ui/SubtitleText';

/**
 * Props for CancelAppointmentModal component
 */
export interface CancelAppointmentModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** URL for the Calendly cancel page */
  cancelUrl: string;
  /** Callback to refresh appointment data */
  onRefresh?: () => void;
}

/**
 * Cancel Appointment Modal Component
 */
export const CancelAppointmentModal: React.FC<CancelAppointmentModalProps> = ({
  isOpen,
  onClose,
  cancelUrl,
  onRefresh
}) => {
  const { isDark } = useTheme();

  // Handle escape key press
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleClose = () => {
    onClose();
    // Call refresh when modal is closed
    if (onRefresh) {
      onRefresh();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div
        className={`absolute inset-0 ${
          isDark ? 'bg-black/70' : 'bg-black/50'
        }`}
        onClick={handleClose}
      />

      {/* Modal Content */}
      <div className={`relative w-full max-w-4xl h-[90vh] mx-4 rounded-lg shadow-xl ${
        isDark ? 'bg-neutral-900' : 'bg-white'
      }`}>
        {/* Modal Header */}
        <div className={`flex items-center justify-between p-4 border-b ${
          isDark ? 'border-neutral-700' : 'border-neutral-200'
        }`}>
          <SubtitleText className="font-semibold">
            Cancel Appointment
          </SubtitleText>
          <button
            onClick={handleClose}
            className={`p-2 rounded-md border transition-colors ${
              isDark
                ? 'border-neutral-500 hover:bg-neutral-700 hover:border-neutral-300'
                : 'border-black hover:bg-neutral-100 hover:border-neutral-600'
            }`}
            aria-label="Close modal"
          >
            <svg className={`w-5 h-5 fill-none stroke-current ${
              isDark ? 'text-neutral-300' : 'text-black'
            }`} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Modal Body - Iframe */}
        <div className="p-4 h-[calc(90vh-80px)]">
          <iframe
            src={cancelUrl}
            className="w-full h-full border-0 rounded"
            title="Cancel Appointment"
            allow="camera; microphone; geolocation; encrypted-media"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
          />
        </div>
      </div>
    </div>
  );
};

export default CancelAppointmentModal;